import React, { useRef, useState, ChangeEvent, useEffect } from "react";
import { formatDuration } from "shared/utils/formatDuration";
import "styles/legacy/UploadNewTitle.css";

type UploadStage = "idle" | "uploading" | "uploaded" | "error";

type UploadSectionProps = {
  title: string;
  description: string;
  formats: string;
  onFileChange: (file: File | null) => void;
  currentFile?: File | null;
  accept?: string;
  uploadStage?: UploadStage;
  progress: number;
  onCancel?: () => void;
  onDelete?: () => void;
  showProgress?: boolean;
  language?: string;
  onLanguageChange?: (lang: string) => void;
  showLanguageSelect?: boolean;
  isInvalid?: boolean;
  isEditingPublished?: boolean;
  allowReplace?: boolean;
};

const extractRealFileName = (fileName: string) => {
  // Match UUID-like patterns and optional leading numbers/underscores
  const pattern =
    /^[\d_]*([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i;
  const match = fileName.match(pattern);
  if (match) {
    // Remove the matched prefix including the UUID and any leading numbers/underscores
    return fileName.substring(match[0].length);
  }
  // If no UUID pattern, return the original filename
  return fileName;
};

const UploadSection: React.FC<UploadSectionProps> = ({
  title,
  description,
  formats,
  onFileChange,
  currentFile = null,
  accept = "*",
  uploadStage = "idle",
  progress = 0,
  onCancel,
  onDelete,
  language,
  onLanguageChange,
  showLanguageSelect,
  isInvalid = false,
  isEditingPublished,
  allowReplace = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [durationStr, setDurationStr] = useState("");

  // Set duration string when a file with duration is loaded
  useEffect(() => {
    if (currentFile && (currentFile as any).duration) {
      setDurationStr(formatDuration((currentFile as any).duration));
    }
  }, [currentFile]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (isEditingPublished || (currentFile && !allowReplace) || uploadStage === "uploading") return;
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      e.target.value = "";

      // Extract image dimensions if it's an image file
      if (file.type.startsWith("image/")) {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          // Set width and height properties on the file object
          Object.assign(file, {
            width: img.width,
            height: img.height,
          });
          onFileChange(file);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          console.error("Failed to load image dimensions");
          onFileChange(file);
        };
        img.src = URL.createObjectURL(file);
        return;
      }

      // For non-image files, proceed as before
      onFileChange(file);

      // Only try to extract duration for audio/video files
      if (file.type.startsWith("audio/") || file.type.startsWith("video/")) {
        const url = URL.createObjectURL(file);
        const media = document.createElement(
          file.type.startsWith("audio/") ? "audio" : "video"
        );
        media.src = url;
        media.onloadedmetadata = () => {
          const duration = media.duration;
          setDurationStr(isNaN(duration) ? "" : formatDuration(duration));
          URL.revokeObjectURL(url);
        };
      } else {
        setDurationStr("");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isEditingPublished || (currentFile && !allowReplace) || uploadStage === "uploading") return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    } else {
      onFileChange(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const formatSize = (size: number) => {
    if (size === 0) return "0 MB";
    return (size / (1024 * 1024)).toFixed(2) + " MB";
  };

  const getFileType = (file: File) => {
    const type = file.type || file.name.split(".").pop() || "unknown";
    return type.split("/").pop()?.toUpperCase() || type.toUpperCase();
  };

  const getImageDimensions = (file: File) => {
    if ((file as any).width && (file as any).height) {
      return ` · ${(file as any).width}x${(file as any).height}`;
    }
    return "";
  };

  const binSVG = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Remove file"
      role="img"
    >
      <path
        d="M3.66667 12.6667C3.66667 13.4 4.26667 14 5 14H10.3333C11.0667 14 11.6667 13.4 11.6667 12.6667V4.66667H3.66667V12.6667ZM12.3333 2.66667H10L9.33333 2H6L5.33333 2.66667H3V4H12.3333V2.66667Z"
        fill="#FAFAFA"
      />
    </svg>
  );

  const checkmarkSVG = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Upload successful"
      role="img"
    >
      <g clipPath="url(#clip0_4916_1540)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM5.98099 9.37272L8.26557 11.5935L14.019 6.00072L15 6.95432L8.26557 13.5007L5 10.3263L5.98099 9.37272Z"
          fill="#4ADE80"
        />
      </g>
      <defs>
        <clipPath id="clip0_4916_1540">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  return (
    <div className="upload-section">
      <div className="upload-section-header">
        <h3 className="upload-section-title">{title}</h3>
        <p className="upload-section-description">{description}</p>
      </div>
      <div
        className={`upload-box ${isDragging ? "dragging" : ""} ${
          uploadStage === "uploaded" ? "uploaded" : ""
        } ${isInvalid ? "error-border" : ""}`}
        onDragOver={isEditingPublished ? undefined : handleDragOver}
        onDragLeave={isEditingPublished ? undefined : handleDragLeave}
        onDrop={isEditingPublished ? undefined : handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={accept}
          style={{ display: "none" }}
          disabled={isEditingPublished || uploadStage === "uploading"}
        />

        {/* --- Stage 1: No file chosen --- */}
        {!currentFile && (
          <>
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.5 15V19C21.5 19.5304 21.2893 20.0391 20.9142 20.4142C20.5391 20.7893 20.0304 21 19.5 21H5.5C4.96957 21 4.46086 20.7893 4.08579 20.4142C3.71071 20.0391 3.5 19.5304 3.5 19V15"
                stroke="#FAFAFA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.5 8L12.5 3L7.5 8"
                stroke="#FAFAFA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.5 3V15"
                stroke="#FAFAFA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="upload-or-drop-formats">
              {isEditingPublished ? (
                <p className="drop-file" style={{ color: "#aeaea0" }}>
                  Media cannot be changed for published titles
                </p>
              ) : (
                <>
                  <p className="drop-file">
                    Drop file here or{" "}
                    <span
                      className="upload-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      style={{ cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          fileInputRef.current?.click();
                        }
                      }}
                    >
                      click here to select from your computer
                    </span>
                  </p>
                  <p className="upload-formats">{formats}</p>
                </>
              )}
            </div>
          </>
        )}

        {/* --- Stage 2: Uploading --- */}
        {currentFile && uploadStage === "uploading" && (
          <>
            <div className="file-row">
              <span className="file-name">
                {extractRealFileName(currentFile.name)}
              </span>
              {!isEditingPublished && (
                <button
                  className="remove-file-btn"
                  onClick={handleRemoveFile}
                  aria-label="Cancel upload"
                >
                  {binSVG}
                </button>
              )}
            </div>
            <div className="file-info-row">
              {formatSize(
                (currentFile as any)?.isPlaceholder
                  ? (currentFile as any).originalSize
                  : currentFile.size
              )}{" "}
              · {getFileType(currentFile)}
              {currentFile.type.startsWith("image/")
                ? getImageDimensions(currentFile)
                : ""}
              {durationStr ? " · " : ""}
              {durationStr}
            </div>
            <div
              className="progress-bar-wrapper"
              aria-label={`Upload progress: ${progress}%`}
            >
              <div className="progress-bar-container">
                <div
                  className="upload-progress-bar"
                  style={{ width: `${progress}%` }}
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <span className="upload-progress-percentage">{progress}%</span>
            </div>
          </>
        )}

        {/* --- Stage 3: Uploaded --- */}
        {uploadStage === "uploaded" && (
          <>
            {!isEditingPublished && allowReplace && <button type="button" onClick={() => fileInputRef.current?.click()}>Replace file</button>}
            <div className="uploaded-file-row">
              <div className="uploaded-file-row-right">
                <span className="checkmark-icon">{checkmarkSVG}</span>
                <span className="file-name">
                  {currentFile
                    ? extractRealFileName(currentFile.name).split(".")[0]
                    : "Subtitle uploaded successfully!"}
                </span>
              </div>

              {onDelete && !isEditingPublished && (
                <button
                  className="remove-file-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete uploaded file"
                >
                  {binSVG}
                </button>
              )}
            </div>
            {currentFile && (
              <div className="file-info-row">
                {formatSize(
                  (currentFile as any)?.isPlaceholder
                    ? (currentFile as any).originalSize
                    : currentFile.size
                )}{" "}
                · {getFileType(currentFile)}
                {currentFile.type.startsWith("image/")
                  ? getImageDimensions(currentFile)
                  : ""}
                {durationStr ? " · " : ""}
                {durationStr}
              </div>
            )}
            {currentFile && showLanguageSelect && (
              <div className="language-select">
                <label
                  htmlFor={`language-select-${currentFile.name}`}
                  className="language-label"
                >
                  Language*
                </label>
                <select
                  id={`language-select-${currentFile.name}`}
                  value={language || ""}
                  onChange={(e) => onLanguageChange?.(e.target.value)}
                  className="language-dropdown"
                  disabled={isEditingPublished}
                >
                  <option value="" disabled>
                    Select the subtitle language
                  </option>
                  <option value="arabic">Arabic</option>
                  <option value="bengali">Bengali</option>
                  <option value="chinese">Chinese</option>
                  <option value="czech">Czech</option>
                  <option value="danish">Danish</option>
                  <option value="dutch">Dutch</option>
                  <option value="english">English</option>
                  <option value="finnish">Finnish</option>
                  <option value="filipino">Filipino</option>
                  <option value="french">French</option>
                  <option value="german">German</option>
                  <option value="greek">Greek</option>
                  <option value="gujarati">Gujarati</option>
                  <option value="hebrew">Hebrew</option>
                  <option value="hindi">Hindi</option>
                  <option value="hungarian">Hungarian</option>
                  <option value="indonesian">Indonesian</option>
                  <option value="italian">Italian</option>
                  <option value="japanese">Japanese</option>
                  <option value="kannada">Kannada</option>
                  <option value="korean">Korean</option>
                  <option value="malay">Malay</option>
                  <option value="marathi">Marathi</option>
                  <option value="norwegian">Norwegian</option>
                  <option value="persian">Persian</option>
                  <option value="polish">Polish</option>
                  <option value="portuguese">Portuguese</option>
                  <option value="romanian">Romanian</option>
                  <option value="russian">Russian</option>
                  <option value="spanish">Spanish</option>
                  <option value="swahili">Swahili</option>
                  <option value="swedish">Swedish</option>
                  <option value="tamil">Tamil</option>
                  <option value="telugu">Telugu</option>
                  <option value="thai">Thai</option>
                  <option value="turkish">Turkish</option>
                  <option value="ukrainian">Ukrainian</option>
                  <option value="vietnamese">Vietnamese</option>
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UploadSection;
